/**
 * ManpowerCalendar.jsx - who is on which project, by day or by week.
 *
 * The rows ARE the engineer cards: `engineers` comes straight from the
 * Engineers tab, so adding someone there adds a row here, and removing them
 * removes it. There is no spreadsheet behind this any more — the Manpower
 * Planning export used to supply both the rows and a baseline of assignments,
 * which meant the grid opened pre-filled with labels nobody here had typed and
 * jumped to whatever month that file last covered.
 *
 * All the schedule state (assignments, labels, colours) belongs to the parent
 * via useManpower, because the cards read the same store — see that file.
 * Pieces live in ./manpower/.
 *
 * `projects` (AppContext's allProjects) is what the editor's Project # picker
 * offers; blocks with no Project # are outlined in the grid and counted in a
 * bar above it, which lists them and opens each one (10/11/26).
 *
 * The grids are the calendar's TABLES (10/16/26): `tables` is
 * useCalendarTables - which tables, in what order, who sits in each. With
 * `editable` (Engineers Edit, as for saving a day) people add, rename,
 * reorder and delete (empty) tables, switch each one's "Counts toward
 * utilization", and move engineers between tables by dragging a name or with
 * its Move to menu. Without `tables` the grids are today's default, Controls
 * above Process by discipline.
 */
import { useState, useMemo, useCallback } from 'react';
import { WEEKS_VISIBLE } from './manpower/manpowerConstants';
import { buildWeeks, daysOfMonth, isoWeekKey, shortDate, ymd, weekSpan } from './manpower/manpowerHelpers';
import { buildAssignments, buildFreeAssignments, blockDetail, latestDetailFor } from './manpower/manpowerAssignments';
import { CalendarGrid } from './manpower/CalendarGrid';
import { ColorCustomizer } from './manpower/ColorCustomizer';
import { CellEditor } from './manpower/CellEditor';
import { DEFAULT_LAYOUT, orderedTables, sectionsOf, tableOf } from './manpower/calendarTables';
import { AddTableButton, MoveToSelect, TableHeaderTools, TableTitle } from './manpower/CalendarTableTools';
import { ENGINEER_DRAG } from './manpower/CalendarGrid';
import { fillCandidates, jobOptions, missingNumberBlocks } from './manpower/projectNumbers';
import { fmtDate } from '../utils/format';

export function ManpowerCalendar({ engineers, mp, projects, tables = null, editable = false }) {
  const {
    edits,
    details,
    customProjects,
    palette,
    syncStatus,
    loaded,
    setPaletteOverride,
    colorFor,
    deleteCustomProject,
    applyEdit,
    moveEdit,
    fillProjectNumber,
  } = mp;

  const [mode, setMode] = useState('monthly'); // 'monthly' | 'weekly' | 'quarterly' | 'yearly'
  // Always the month you are actually in. It used to hunt for the newest month
  // the planning sheet had data for and jump there, which is how the calendar
  // came up on January 2027.
  const [monthRef, setMonthRef] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [quarterRef, setQuarterRef] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1);
  });
  const [yearRef, setYearRef] = useState(() => new Date(new Date().getFullYear(), 0, 1));

  // The week axis, generated rather than read out of a file — see buildWeeks.
  const allWeeks = useMemo(() => buildWeeks(), []);
  const todayKey = isoWeekKey(new Date());
  const initialWeekStart = useMemo(() => {
    const i = allWeeks.findIndex((w) => w.iso === todayKey);
    return i >= 0 ? Math.max(0, i - 1) : 0;
  }, [todayKey, allWeeks]);
  const [weekStart, setWeekStart] = useState(initialWeekStart);

  const [showColors, setShowColors] = useState(false);
  const [focusCode, setFocusCode] = useState(null);
  const [editor, setEditor] = useState(null); // { engineerName, dateFrom, dateTo, initialCode, detail }
  // The missing-Project # bar: its list open, past blocks in it, and the grid
  // faded down to just the flagged blocks.
  const [missingOpen, setMissingOpen] = useState(false);
  const [missingPast, setMissingPast] = useState(false);
  const [onlyMissing, setOnlyMissing] = useState(false);

  const jobs = useMemo(() => jobOptions(projects), [projects]);
  const todayYmd = ymd(new Date());
  // Only engineers on the calendar: a departed engineer's old rows cannot be opened here.
  const rosterNames = useMemo(() => new Set((engineers || []).map((e) => e.name)), [engineers]);
  const missingAll = useMemo(
    () => missingNumberBlocks(edits, details || {}).filter((b) => rosterNames.has(b.engineerName)),
    [edits, details, rosterNames]
  );
  const missingNow = useMemo(() => missingAll.filter((b) => b.to >= todayYmd), [missingAll, todayYmd]);
  const missingShown = missingPast ? missingAll : missingNow;
  const editorFill = useCallback(
    (scope, label, from, to) =>
      editor ? fillCandidates(edits, details || {}, { engineerName: editor.engineerName, from, to, label, scope }) : [],
    [edits, details, editor]
  );

  // Opened on an existing block, the editor starts from that block's details
  // (its first day that has any); an empty cell starts blank.
  const openEditor = (cell) =>
    setEditor({
      ...cell,
      detail: cell.initialCode
        ? blockDetail(
            edits,
            details || {},
            cell.engineerName,
            cell.clearFrom || cell.dateFrom,
            cell.clearTo || cell.dateTo,
            cell.initialCode
          )
        : null,
    });

  // Rows come from the engineer cards. `subtitle` is that card's own Skill
  // text, shown as-is: the calendar does not caption anyone.
  const rows = useMemo(
    () =>
      (engineers || []).map((e) => ({
        id: e.id,
        name: e.name,
        rawName: e.name,
        subtitle: e.skill && e.skill !== '-' ? e.skill : '',
        // Which table: the one they were moved to, else by discipline (a
        // Mechanical engineer with Controls). See manpower/calendarTables.js.
        discipline: e.discipline,
      })),
    [engineers]
  );

  const layout = (tables && tables.layout) || DEFAULT_LAYOUT;
  const manage = !!(tables && editable);
  const sections = useMemo(() => sectionsOf(rows, layout), [rows, layout]);
  const tableList = useMemo(() => orderedTables(layout), [layout]);
  // The table a dragged name is over, for the drop outline.
  const [dropOver, setDropOver] = useState(null);
  const moveTo = (row, tableId) => {
    if (!tables || row.id == null || tableOf(row, layout) === tableId) return;
    tables.moveEngineer(row, tableId);
  };
  const dropFor = (sec) =>
    manage
      ? {
          over: dropOver === sec.id,
          onOver: (on) => setDropOver((cur) => (on ? sec.id : cur === sec.id ? null : cur)),
          onDragStart: (row, ev) => {
            if (row.id == null) {
              ev.preventDefault();
              return;
            }
            ev.dataTransfer.setData(ENGINEER_DRAG, String(row.id));
            ev.dataTransfer.effectAllowed = 'move';
          },
          onDrop: (ev) => {
            const id = ev.dataTransfer.getData(ENGINEER_DRAG);
            const row = rows.find((r) => String(r.id) === id);
            if (row) moveTo(row, sec.id);
          },
        }
      : null;

  const columns = useMemo(() => {
    if (mode === 'monthly') {
      return daysOfMonth(monthRef).map((d) => {
        const dow = d.getDay();
        return {
          key: ymd(d),
          from: ymd(d),
          to: ymd(d),
          label: String(d.getDate()),
          sublabel: ['S', 'M', 'T', 'W', 'T', 'F', 'S'][dow],
          isWeekend: dow === 0 || dow === 6,
          weekKey: isoWeekKey(d),
        };
      });
    }
    if (mode === 'quarterly') {
      const qStart = new Date(quarterRef);
      const qEnd = new Date(qStart.getFullYear(), qStart.getMonth() + 3, 0);
      // Compare the YYYY-MM-DD strings: new Date('2026-04-06') is UTC midnight,
      // which put a week starting on a quarter's first Monday in no quarter.
      return allWeeks
        .filter((w) => w.monday >= ymd(qStart) && w.monday <= ymd(qEnd))
        .map((w) => ({
          key: w.iso,
          ...weekSpan(w.monday),
          label: 'Wk ' + w.iso.split('-W')[1],
          sublabel: shortDate(w.monday),
          isWeekend: false,
          weekKey: w.iso,
        }));
    }
    if (mode === 'yearly') {
      const y = yearRef.getFullYear();
      const out = [];
      for (let m = 0; m < 12; m++) {
        const d = new Date(y, m, 1);
        out.push({
          key: `${y}-${String(m + 1).padStart(2, '0')}`,
          from: ymd(d),
          to: ymd(new Date(y, m + 1, 0)),
          label: d.toLocaleDateString('en-US', { month: 'short' }),
          sublabel: String(y),
          isWeekend: false,
          monthIndex: m,
          year: y,
        });
      }
      return out;
    }
    // weekly
    return allWeeks.slice(weekStart, weekStart + WEEKS_VISIBLE).map((w) => ({
      key: w.iso,
      ...weekSpan(w.monday),
      label: 'Wk ' + w.iso.split('-W')[1],
      sublabel: shortDate(w.monday),
      isWeekend: false,
      weekKey: w.iso,
    }));
  }, [mode, monthRef, weekStart, quarterRef, yearRef, allWeeks]);

  const todayCol = useMemo(() => {
    const t = new Date();
    if (mode === 'monthly') {
      return t.getFullYear() === monthRef.getFullYear() && t.getMonth() === monthRef.getMonth() ? ymd(t) : null;
    }
    if (mode === 'yearly') {
      return t.getFullYear() === yearRef.getFullYear() ? `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}` : null;
    }
    return todayKey; // weekly + quarterly are keyed by ISO week
  }, [mode, monthRef, yearRef, todayKey]);

  const weeklyWindow = useMemo(() => allWeeks.slice(weekStart, weekStart + WEEKS_VISIBLE), [weekStart, allWeeks]);
  // Which days the assignment builder has to bucket, per mode.
  const days = useMemo(() => {
    if (mode === 'monthly') return daysOfMonth(monthRef);
    if (mode === 'yearly') {
      const y = yearRef.getFullYear();
      const out = [];
      for (let m = 0; m < 12; m++) {
        const last = new Date(y, m + 1, 0).getDate();
        for (let d = 1; d <= last; d++) out.push(new Date(y, m, d));
      }
      return out;
    }
    return [];
  }, [mode, monthRef, yearRef]);

  const visibleWeeks = useMemo(() => {
    if (mode === 'weekly') return weeklyWindow;
    if (mode === 'quarterly') {
      const qStart = new Date(quarterRef);
      const qEnd = new Date(qStart.getFullYear(), qStart.getMonth() + 3, 0);
      // Compare the YYYY-MM-DD strings: new Date('2026-04-06') is UTC midnight,
      // which put a week starting on a quarter's first Monday in no quarter.
      return allWeeks.filter((w) => w.monday >= ymd(qStart) && w.monday <= ymd(qEnd));
    }
    return [];
  }, [mode, weeklyWindow, quarterRef, allWeeks]);

  const sectionAssignments = useMemo(
    () => sections.map((sec) => buildAssignments(sec.engineers, mode, days, visibleWeeks, edits, details)),
    [sections, mode, days, visibleWeeks, edits, details]
  );
  // The same columns inside out, for each grid's Availability button.
  const sectionFree = useMemo(() => sections.map((sec) => buildFreeAssignments(sec.engineers, columns, edits)), [sections, columns, edits]);

  // Project labels on screen right now (for the colour legend).
  const visibleProjects = useMemo(() => {
    const set = new Set();
    for (const m of sectionAssignments) {
      for (const bucket of m.values()) for (const entry of bucket.values()) set.add(entry.code);
    }
    return [...set].sort();
  }, [sectionAssignments]);

  // Every label the editor offers — all of them typed by somebody here.
  const allKnownProjects = useMemo(() => {
    const set = new Set(customProjects);
    for (const v of Object.values(edits)) if (v) set.add(v);
    return [...set].sort();
  }, [edits, customProjects]);

  const monthLabel = monthRef.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const quarterLabel = `Q${Math.floor(quarterRef.getMonth() / 3) + 1} ${quarterRef.getFullYear()}`;
  const yearLabel = String(yearRef.getFullYear());
  const stepMonth = (d) => setMonthRef(new Date(monthRef.getFullYear(), monthRef.getMonth() + d, 1));
  const stepQuarter = (d) => setQuarterRef(new Date(quarterRef.getFullYear(), quarterRef.getMonth() + d * 3, 1));
  const stepYear = (d) => setYearRef(new Date(yearRef.getFullYear() + d, 0, 1));
  const jumpToToday = () => {
    const t = new Date();
    setMonthRef(new Date(t.getFullYear(), t.getMonth(), 1));
    setQuarterRef(new Date(t.getFullYear(), Math.floor(t.getMonth() / 3) * 3, 1));
    setYearRef(new Date(t.getFullYear(), 0, 1));
    setWeekStart(initialWeekStart);
  };

  const openColorEditor = (code) => {
    setFocusCode(code);
    setShowColors(true);
  };

  // A listed block: show its month and open it, as a click on the bar would.
  const openMissing = (b) => {
    const [y, m] = b.from.split('-').map(Number);
    setMode('monthly');
    setMonthRef(new Date(y, m - 1, 1));
    openEditor({ engineerName: b.engineerName, dateFrom: b.from, dateTo: b.to, initialCode: b.label, clearFrom: b.from, clearTo: b.to });
  };

  return (
    <div>
      <div
        style={{
          background: '#fff',
          border: '1px solid #E5E9DD',
          borderRadius: 12,
          padding: '14px 18px',
          marginBottom: 14,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, color: '#1B5E20', letterSpacing: 1.8, textTransform: 'uppercase' }}>Manpower</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#0D0D0D', marginTop: 2 }}>Engineer Capacity Calendar</div>
          {/* No standing caption here. It used to read "Mirrors the Manpower
              Planning sheet" followed by instructions and a permanent
              "synced" - a line that was part stale, part telling you what a
              tooltip already says, and part reassurance nobody needed. The
              cells carry their own hints on hover, and the only state worth
              interrupting for is a save that did not work, below. Unsaved
              changes outlive leaving this tab, not a page reload - hence
              "keep this page open". 'reload': the server asked for one. */}
          {(syncStatus === 'error' || syncStatus === 'reload') && (
            <div style={{ fontSize: 11, color: '#B71C1C', fontWeight: 700, marginTop: 3 }}>
              {syncStatus === 'reload'
                ? 'GlobalOps was updated - reload the page.'
                : 'Not saved yet - trying again. Keep this page open until this clears.'}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', background: '#F4F6F0', borderRadius: 8, padding: 3, gap: 2 }}>
            {['monthly', 'weekly', 'quarterly', 'yearly'].map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 6,
                  border: 'none',
                  background: mode === m ? '#1B5E20' : 'transparent',
                  color: mode === m ? '#fff' : '#444',
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                  textTransform: 'capitalize',
                }}
              >
                {m}
              </button>
            ))}
          </div>
          {mode === 'monthly' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => stepMonth(-1)} style={navBtn(true)}>
                ← Prev
              </button>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0D0D0D', minWidth: 130, textAlign: 'center' }}>{monthLabel}</div>
              <button onClick={() => stepMonth(1)} style={navBtn(true)}>
                Next →
              </button>
              <button onClick={jumpToToday} style={{ ...navBtn(true), background: '#1B5E20', color: '#fff', border: '1px solid #0F3D14' }}>
                This month
              </button>
            </div>
          )}
          {mode === 'weekly' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                onClick={() => setWeekStart(Math.max(0, weekStart - WEEKS_VISIBLE))}
                disabled={weekStart === 0}
                style={navBtn(weekStart > 0)}
              >
                ← Earlier
              </button>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0D0D0D', minWidth: 130, textAlign: 'center' }}>
                {weeklyWindow.length
                  ? `${shortDate(weeklyWindow[0].monday)} — ${shortDate(weeklyWindow[weeklyWindow.length - 1].monday)}`
                  : ''}
              </div>
              <button
                onClick={() => setWeekStart(Math.min(allWeeks.length - WEEKS_VISIBLE, weekStart + WEEKS_VISIBLE))}
                style={navBtn(true)}
              >
                Later →
              </button>
              <button onClick={jumpToToday} style={{ ...navBtn(true), background: '#1B5E20', color: '#fff', border: '1px solid #0F3D14' }}>
                This week
              </button>
            </div>
          )}
          {mode === 'quarterly' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => stepQuarter(-1)} style={navBtn(true)}>
                ← Prev
              </button>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0D0D0D', minWidth: 130, textAlign: 'center' }}>{quarterLabel}</div>
              <button onClick={() => stepQuarter(1)} style={navBtn(true)}>
                Next →
              </button>
              <button onClick={jumpToToday} style={{ ...navBtn(true), background: '#1B5E20', color: '#fff', border: '1px solid #0F3D14' }}>
                This quarter
              </button>
            </div>
          )}
          {mode === 'yearly' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button onClick={() => stepYear(-1)} style={navBtn(true)}>
                ← Prev
              </button>
              <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0D0D0D', minWidth: 130, textAlign: 'center' }}>{yearLabel}</div>
              <button onClick={() => stepYear(1)} style={navBtn(true)}>
                Next →
              </button>
              <button onClick={jumpToToday} style={{ ...navBtn(true), background: '#1B5E20', color: '#fff', border: '1px solid #0F3D14' }}>
                This year
              </button>
            </div>
          )}
          {manage && <AddTableButton onAdd={tables.addTable} />}
          <button
            onClick={() => {
              setFocusCode(null);
              setShowColors(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid #E5E9DD',
              background: '#fff',
              color: '#1B5E20',
              fontSize: 11,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'Montserrat',
            }}
          >
            <span style={{ width: 12, height: 12, borderRadius: 3, background: 'linear-gradient(135deg, #92D050, #FFC000, #FF00FF)' }} />
            Customize colors
          </button>
        </div>
      </div>

      {/* Project legend — every label shown in the current view */}
      {visibleProjects.length > 0 && (
        <div
          style={{
            background: '#fff',
            border: '1px solid #E5E9DD',
            borderRadius: 10,
            padding: '10px 18px',
            marginBottom: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontSize: 9.5, fontWeight: 800, color: '#888', letterSpacing: 1.3, textTransform: 'uppercase' }}>
              Projects ({visibleProjects.length}) — click a swatch to recolor
            </span>
            <span style={{ fontSize: 10, color: '#999', fontWeight: 500 }}>
              {mode === 'monthly'
                ? monthLabel
                : mode === 'quarterly'
                  ? quarterLabel
                  : mode === 'yearly'
                    ? yearLabel
                    : `${shortDate(weeklyWindow[0]?.monday)} — ${shortDate(weeklyWindow[weeklyWindow.length - 1]?.monday)}`}
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {visibleProjects.map((code) => (
              <button
                key={code}
                onClick={() => openColorEditor(code)}
                title="Click to change this project's color"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 10px 4px 4px',
                  borderRadius: 999,
                  background: '#fff',
                  border: '1px solid #E5E9DD',
                  fontSize: 10.5,
                  fontWeight: 700,
                  fontFamily: 'JetBrains Mono, monospace',
                  color: '#0D0D0D',
                  cursor: 'pointer',
                  transition: 'border-color 0.1s',
                }}
                onMouseEnter={(ev) => (ev.currentTarget.style.borderColor = '#1B5E20')}
                onMouseLeave={(ev) => (ev.currentTarget.style.borderColor = '#E5E9DD')}
              >
                <span
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    background: colorFor(code),
                    flexShrink: 0,
                    border: '1px solid rgba(0,0,0,0.08)',
                  }}
                />
                {code}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Blocks with no Project #: engineers see no GLOBAL # for them on My
          Schedule / My Project. Current and upcoming ones by default. */}
      {loaded && (missingNow.length > 0 || (missingPast && missingAll.length > 0) || onlyMissing) && (
        <div
          data-testid="missing-number-bar"
          style={{
            background: '#FFF5F5',
            border: '1px solid #F5C2C2',
            borderLeft: '4px solid #D32F2F',
            borderRadius: 10,
            padding: '9px 16px',
            marginBottom: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#B71C1C' }}>
              {`${missingShown.length} block${missingShown.length === 1 ? '' : 's'} missing a Project #`}
            </span>
            <span style={{ fontSize: 10.5, color: '#7F1D1D', fontWeight: 600 }}>
              {missingPast ? 'all dates' : 'current and upcoming'} - engineers see no GLOBAL # for these on My Schedule / My Project.
            </span>
            <span style={{ flex: 1 }} />
            <button type="button" onClick={() => setMissingOpen((v) => !v)} style={missingBtn(missingOpen)}>
              {missingOpen ? 'Hide list' : 'Show list'}
            </button>
            <button type="button" aria-pressed={onlyMissing} onClick={() => setOnlyMissing((v) => !v)} style={missingBtn(onlyMissing)}>
              {onlyMissing ? '\u2713 Only missing' : 'Only missing'}
            </button>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                fontSize: 10.5,
                fontWeight: 700,
                color: '#7F1D1D',
                cursor: 'pointer',
              }}
            >
              <input type="checkbox" checked={missingPast} onChange={(e) => setMissingPast(e.target.checked)} />
              Include past
            </label>
          </div>
          {missingOpen && (
            <div style={{ marginTop: 8, maxHeight: 220, overflowY: 'auto', borderTop: '1px solid #F5C2C2' }}>
              {missingShown.length === 0 && <div style={{ padding: '8px 2px', fontSize: 11, color: '#666' }}>Nothing missing.</div>}
              {missingShown.map((b) => (
                <button
                  key={`${b.engineerName}|${b.from}`}
                  type="button"
                  onClick={() => openMissing(b)}
                  title="Open this block to pick its project"
                  style={{
                    display: 'flex',
                    gap: 10,
                    width: '100%',
                    textAlign: 'left',
                    alignItems: 'baseline',
                    padding: '6px 2px',
                    border: 'none',
                    borderBottom: '1px solid #FBE3E3',
                    background: 'transparent',
                    cursor: 'pointer',
                    fontFamily: 'Montserrat',
                    fontSize: 11,
                    color: '#0D0D0D',
                  }}
                >
                  <span style={{ fontWeight: 800, minWidth: 150 }}>{b.engineerName}</span>
                  <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, flex: 1 }}>{b.label}</span>
                  <span style={{ color: '#666', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {b.from === b.to ? fmtDate(b.from) : `${fmtDate(b.from)} \u2192 ${fmtDate(b.to)}`} · {b.days}d
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* A table the server refused a change to (a name taken, people still
          in it...): why, until the next change or a dismiss. */}
      {manage && tables.error && (
        <div
          role="alert"
          style={{
            background: '#FFF5F5',
            border: '1px solid #F5C2C2',
            borderRadius: 10,
            padding: '8px 14px',
            marginBottom: 14,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            fontSize: 11.5,
            fontWeight: 700,
            color: '#B71C1C',
          }}
        >
          <span style={{ flex: 1 }}>{tables.error}</span>
          <button type="button" onClick={tables.clearError} style={missingBtn(false)}>
            Dismiss
          </button>
        </div>
      )}

      {/* The tables, in their order (Controls above Process to start). An
          empty table is drawn only when it can be managed - to drop people in
          or delete it. */}
      {sections.map((sec, i) =>
        sec.engineers.length === 0 && !manage ? null : (
          <CalendarGrid
            key={sec.id}
            testId={`calendar-table-${sec.id}`}
            title={<TableTitle name={sec.name} onRename={manage ? (name) => tables.renameTable(sec.id, name) : undefined} />}
            headerTools={
              manage ? (
                <TableHeaderTools
                  table={sec}
                  first={i === 0}
                  last={i === sections.length - 1}
                  empty={sec.engineers.length === 0}
                  onlyTable={sections.length === 1}
                  onCounts={(on) => tables.setCounts(sec.id, on)}
                  onShift={(dir) => tables.shiftTable(sec.id, dir)}
                  onDelete={() => tables.deleteTable(sec.id)}
                />
              ) : null
            }
            rowTools={
              manage
                ? (row) =>
                    row.id == null ? null : (
                      <MoveToSelect name={row.name} tables={tableList} current={sec.id} onMove={(to) => moveTo(row, to)} />
                    )
                : null
            }
            drop={dropFor(sec)}
            engineers={sec.engineers}
            columns={columns}
            mode={mode}
            assignments={sectionAssignments[i]}
            freeAssignments={sectionFree[i]}
            loaded={loaded}
            loadFailed={!loaded && syncStatus === 'error'}
            todayCol={todayCol}
            colorFor={colorFor}
            onProjectClick={openColorEditor}
            onCellClick={openEditor}
            onlyMissing={onlyMissing}
          />
        )
      )}

      {showColors && (
        <ColorCustomizer
          projects={allKnownProjects}
          customProjects={customProjects}
          palette={palette}
          setPaletteOverride={setPaletteOverride}
          onDeleteCustomProject={deleteCustomProject}
          onClose={() => setShowColors(false)}
          focusCode={focusCode}
        />
      )}

      {editor && (
        <CellEditor
          initial={editor}
          allProjects={allKnownProjects}
          colorFor={colorFor}
          suggestDetail={(label) => latestDetailFor(edits, details || {}, label)}
          jobs={jobs}
          fillCandidates={fillProjectNumber ? editorFill : undefined}
          onFill={fillProjectNumber}
          // Opened on an existing block: Save MOVES it (old days cleared,
          // new ones written). Opened on an empty cell: Save just adds.
          onSave={(from, to, project, detail) =>
            editor.initialCode
              ? moveEdit(
                  editor.engineerName,
                  { from: editor.clearFrom || editor.dateFrom, to: editor.clearTo || editor.dateTo, code: editor.initialCode },
                  from,
                  to,
                  project,
                  detail
                )
              : applyEdit(editor.engineerName, from, to, project, detail)
          }
          // Remove with the dates untouched = remove the whole block, weekend
          // days included; with dates changed = remove just those dates.
          onClear={(from, to) =>
            editor.initialCode && from === editor.dateFrom && to === editor.dateTo
              ? moveEdit(
                  editor.engineerName,
                  { from: editor.clearFrom || from, to: editor.clearTo || to, code: editor.initialCode },
                  from,
                  to,
                  ''
                )
              : applyEdit(editor.engineerName, from, to, '')
          }
          onClose={() => setEditor(null)}
        />
      )}
    </div>
  );
}

function missingBtn(on) {
  return {
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 10.5,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'Montserrat',
    border: '1px solid #D32F2F',
    background: on ? '#D32F2F' : '#fff',
    color: on ? '#fff' : '#B71C1C',
  };
}

function navBtn(enabled) {
  return {
    background: enabled ? '#fff' : '#F4F6F0',
    border: '1px solid #E5E9DD',
    color: enabled ? '#1B5E20' : '#BBB',
    padding: '6px 12px',
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    cursor: enabled ? 'pointer' : 'not-allowed',
    fontFamily: 'Montserrat',
  };
}
